import {
  CartPromotionGroup,
  ICustomerTierDescription,
  ITierDescription,
} from '@teko/cart/v2';
import { DiscountTagColored, InforOutlined } from '@teko/ds-icon';
import React, { useMemo, useState } from 'react';
import { t } from 'i18n';
import {
  styled,
  Body,
  ThemeProps,
  Shape,
  Row,
  Col,
  Modal,
  ModalBody,
} from '@teko/ds-desktop';
import { BottomSheet } from '@teko/ds-mobile';
import { flattenPromotionsFromPromotionGroups } from 'modules/cart/utils';
import { useTenantConfig } from 'hooks';

const MAX_DISPLAY_DESCRIPTIONS = 3;

interface ITierPromotionsProps {
  productPromotions: CartPromotionGroup[];
  // to show text not applicable with combo or not
  isApplicableWithCombo?: boolean;
  maxLinesBeforeEllipsis?: number;
  // epicOmni1585 show promotions for customer tier
  isCustomerTierPromotions?: boolean;
}

const TierPromotions: React.FC<ITierPromotionsProps> = (props) => {
  const {
    productPromotions,
    isApplicableWithCombo = true,
    maxLinesBeforeEllipsis = 1,
    isCustomerTierPromotions = false,
  } = props;
  const { isMobile } = useTenantConfig();
  const [showDetail, setShowDetail] = useState(false);

  // all promotions of an item (not being grouped)
  const allItemPromotions =
    flattenPromotionsFromPromotionGroups(productPromotions);

  // all tier descriptions of an item (not being grouped)
  const allItemTierDescriptions = useMemo(() => {
    const flattedTierDescriptions = !!allItemPromotions.length
      ? allItemPromotions.reduce<ITierDescription[]>(
          (previousTierDescriptions, currentPromotion) => [
            ...previousTierDescriptions,
            ...(currentPromotion.tierDescriptions || []),
          ],
          [],
        )
      : [];
    return flattedTierDescriptions.length > 0
      ? // filter out `tierDescriptions` with empty `descriptions` array
        flattedTierDescriptions.filter(
          (tierDesc) => !!tierDesc.descriptions.length,
        )
      : [];
  }, [allItemPromotions]);

  const allItemHightLightDescriptions = useMemo(() => {
    const flattedHightLightDescriptions = !!allItemPromotions.length
      ? allItemPromotions.reduce<string[]>(
          (previousHightLightDescriptions, currentPromotion) =>
            !!currentPromotion.highlightedDescriptions?.length
              ? [
                  ...previousHightLightDescriptions,
                  ...currentPromotion.highlightedDescriptions,
                ]
              : previousHightLightDescriptions,
          [],
        )
      : [];
    return flattedHightLightDescriptions;
  }, [allItemPromotions]);

  const allItemCustomerTierDescription = useMemo(() => {
    const flattedCustomerTierDescriptions = !!allItemPromotions.length
      ? allItemPromotions.reduce<ICustomerTierDescription[]>(
          (previousTierDescriptions, currentPromotion) => [
            ...previousTierDescriptions,
            ...(currentPromotion.customerTierDescriptions || []),
          ],
          [],
        )
      : [];
    return flattedCustomerTierDescriptions;
  }, [allItemPromotions]);

  // displaying tier descriptions limit to MAX_DISPLAY_DESCRIPTIONS
  const displayTierDescriptions = useMemo(() => {
    return allItemTierDescriptions.length > MAX_DISPLAY_DESCRIPTIONS
      ? allItemTierDescriptions.slice(0, MAX_DISPLAY_DESCRIPTIONS)
      : allItemTierDescriptions;
  }, [allItemTierDescriptions]);

  const handleCloseDetailModal = () => {
    setShowDetail(false);
  };

  const renderDetailContent = () => {
    return allItemTierDescriptions.map((tierDesc, index) => {
      return tierDesc.descriptions?.length > 0 ? (
        <TierDescList
          key={index}
          marginBottom={
            index !== allItemTierDescriptions.length - 1 ? '8px' : '0'
          }
        >
          <b>{tierDesc.title}</b>
          :<br />
          {tierDesc.descriptions.map((desc, subIndex) => {
            return <li key={`${index}-${subIndex}`}>{desc}</li>;
          })}
        </TierDescList>
      ) : null;
    });
  };

  if (
    (!allItemTierDescriptions.length && !isCustomerTierPromotions) ||
    (isCustomerTierPromotions && !allItemCustomerTierDescription.length)
  )
    return null;

  return (
    <>
      {!!displayTierDescriptions.length ||
      (!!allItemCustomerTierDescription.length && isCustomerTierPromotions) ? (
        <Shape
          border="1px solid #FFEED9"
          borderRadius="s"
          backgroundColor="#FFFBF5"
          padding="s8"
        >
          <Row justify="start" align="middle" marginBottom="s4">
            <StyledCol>
              <DiscountTagColored size={20} color="#FFA940" />
              <Body
                weight="bold"
                color="#FA8C16"
                margin={['s0', 's0', 's0', 's4']}
              >
                {isCustomerTierPromotions
                  ? t('Ưu đãi khách hàng thành viên')
                  : t('Mua nhiều thêm ưu đãi')}
              </Body>
            </StyledCol>
          </Row>
          <Row paddingLeft="s8" marginBottom="s4" justify="start">
            <Col>
              <Shape borderLeft="2px solid #FFEED9" padding={['s0', 's12']}>
                {isCustomerTierPromotions
                  ? allItemCustomerTierDescription.map((tierDesc, index) => {
                      return !!tierDesc.description ? (
                        <Body
                          weight="regular"
                          line={maxLinesBeforeEllipsis}
                          color="#FA8C16"
                          key={index}
                        >
                          <StyledText fontWeight="bold" color="#FA8C16">
                            {tierDesc.title},{' '}
                          </StyledText>
                          <StyledText color="#FA8C16">
                            {tierDesc.description}
                          </StyledText>
                        </Body>
                      ) : null;
                    })
                  : displayTierDescriptions.map((tierDesc, index) => {
                      return tierDesc.descriptions?.length > 0 ? (
                        <Body
                          weight="regular"
                          line={maxLinesBeforeEllipsis}
                          color="#FA8C16"
                          key={index}
                        >
                          <StyledText fontWeight="bold" color="#FA8C16">
                            {tierDesc.title},{' '}
                          </StyledText>
                          <StyledText color="#FA8C16">
                            {tierDesc.descriptions?.join(' & ')}
                          </StyledText>
                        </Body>
                      ) : null;
                    })}
              </Shape>
            </Col>
          </Row>

          <Row
            justify={
              !isApplicableWithCombo || !!allItemHightLightDescriptions.length
                ? 'space-between'
                : 'end'
            }
            align="bottom"
          >
            <Col>
              {!isApplicableWithCombo && (
                <Row
                  align="middle"
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                  }}
                >
                  <InforOutlined size={16} color="#FAAD14" />
                  <Body
                    weight="regular"
                    color="#FAAD14"
                    margin={['s0', 's0', 's0', 's4']}
                  >
                    {t('Không áp dụng cùng combo')}
                  </Body>
                </Row>
              )}
              {!!allItemHightLightDescriptions.length && (
                <Row
                  align="middle"
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                  }}
                >
                  <InforOutlined size={16} color="#FAAD14" />
                  <Body
                    weight="regular"
                    color="#FAAD14"
                    margin={['s0', 's0', 's0', 's4']}
                  >
                    {allItemHightLightDescriptions[0]}
                  </Body>
                </Row>
              )}
            </Col>
            {!isCustomerTierPromotions && (
              <Col>
                <StyledDetailButton
                  level={1}
                  onClick={() => setShowDetail(true)}
                >
                  {t('Xem chi tiết')}
                </StyledDetailButton>
              </Col>
            )}
          </Row>
        </Shape>
      ) : null}

      {!!showDetail &&
        (!!isMobile ? (
          <StyledBottomSheet
            show={showDetail}
            title={t('Chi tiết khuyến mãi')}
            closeBottomSheet={handleCloseDetailModal}
            backdropClicked={handleCloseDetailModal}
            contentHeight="500px"
            zIndex={1060}
          >
            <Shape padding={['s4', 's16']}>{renderDetailContent()}</Shape>
          </StyledBottomSheet>
        ) : (
          <StyledModal
            width={600}
            visible={showDetail}
            title={t('Chi tiết khuyến mãi')}
            centeredTitle
            okText=""
            cancelText={t('Đóng')}
            onCancel={handleCloseDetailModal}
            onClose={handleCloseDetailModal}
            showCloseIcon={false}
          >
            <StyledModalBody>{renderDetailContent()}</StyledModalBody>
          </StyledModal>
        ))}
    </>
  );
};

export default TierPromotions;

const StyledDetailButton = styled(Body)(
  ({ theme }: ThemeProps) => `
  cursor: pointer;
  color: ${theme?.colors.link500};

  :hover {
    color: ${theme?.colors.error500};
  }

  &.is-disabled {
    opacity: 0.5;
    cursor: default;
    color: ${theme?.colors.textSecondary};
  }
`,
);

const StyledModal = styled(Modal)`
  padding: 24px;
`;

const StyledModalBody = styled(ModalBody)`
  padding: 0;
  max-height: 600px;
  overflow-y: auto;
`;

const TierDescList = styled.ul(
  ({ marginBottom }: { marginBottom: string | number }) => `
  list-style-position: inside;
  margin-bottom: ${marginBottom};
`,
);

const StyledText = styled.span(
  ({
    color,
    fontWeight,
    fontSize = '13px',
    margin,
  }: {
    color?: string;
    fontWeight?: string;
    fontSize?: string;
    margin?: string;
  }) => `
  color: ${color};
  font-weight: ${fontWeight};
  font-size: ${fontSize};
  margin: ${margin}
`,
);

const StyledCol = styled(Col)`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledBottomSheet = styled(BottomSheet)`
  width: 100%;
`;
